/*
Thunks for the tokenized inspection-scheduling page (STREAM-38).

The page a property contact reaches from the scheduling SMS link. Every thunk
returns the axios response (Consent-style — the page keeps the payload in local
state) and normalizes failures to { error, status, data } so the component can
branch on invalid/expired links and rate limits without try/catch at every
call site.
*/

import { apiRequest } from '../../helpers/Request'

const FALLBACK_ERROR = 'Something went wrong. Please try again.'

const errorFromResponse = (err) =>
  (err.response && err.response.data && err.response.data.error) || FALLBACK_ERROR

const normalizeFailure = (err) => ({
  error: errorFromResponse(err),
  status: err.response && err.response.status,
  data: err.response && err.response.data,
})

export const loadSchedulingPage = token => async () => {
  try {
    const { data } = await apiRequest({
      endpoint: '/inspection-scheduling/' + token,
      method: 'get',
    })
    return { data }
  } catch (err) {
    return normalizeFailure(err)
  }
}

export const submitSchedulingPage = ({ token, availability, answers }) => async () => {
  try {
    const { data } = await apiRequest({
      endpoint: '/inspection-scheduling/' + token,
      method: 'put',
      data: { availability, answers },
    })
    return { data }
  } catch (err) {
    return normalizeFailure(err)
  }
}

// "This isn't me — wrong contact": marks the recipient wrong for this order,
// with an optional free-text suggestion for who to reach instead.
export const reportWrongContact = ({ token, suggestedContact }) => async () => {
  try {
    const { data } = await apiRequest({
      endpoint: '/inspection-scheduling/' + token + '/wrong-contact',
      method: 'post',
      data: { suggested_contact: suggestedContact || null },
    })
    return { data }
  } catch (err) {
    return normalizeFailure(err)
  }
}

// "None of these times work": free-text availability + preferred channel so
// the lender's team can reach out and find a time manually.
export const submitEscalation = ({ token, availabilityText, preferredChannel }) => async () => {
  try {
    const { data } = await apiRequest({
      endpoint: '/inspection-scheduling/' + token + '/escalation',
      method: 'post',
      data: { availability_text: availabilityText, preferred_channel: preferredChannel },
    })
    return { data }
  } catch (err) {
    return normalizeFailure(err)
  }
}

// Expired-link path: asks the backend to text a fresh scheduling link
// (consent-gated + rate-limited server-side; a 429 means one was sent
// recently).
export const requestNewLink = token => async () => {
  try {
    const { data } = await apiRequest({
      endpoint: '/inspection-scheduling/' + token + '/request-link',
      method: 'post',
      data: {},
    })
    return { data }
  } catch (err) {
    return normalizeFailure(err)
  }
}
